import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { WidgetType } from '@noorview/core';
import { WIDGETS, WIDGET_ORDER, createWidget, keepShape, placeNew, widgetName } from '@noorview/core';
import { useApp } from '../data/store';
import { openSheet, useStudioPanels } from '../app/ui';
import { ScrollArea } from '../ui/ScrollArea';
import { Icon } from '../ui/icons';
import { Segmented } from '../ui/controls';
import { useStudio } from './store';
import { useDnd } from './Canvas';

export function Library() {
  const [tab, setTab] = useState<'widgets' | 'layers'>('widgets');
  return (
    <section className="card glass library" aria-label="Widget library">
      <Segmented label="Library" value={tab} onChange={setTab} options={[{ value: 'widgets', label: 'Widgets' }, { value: 'layers', label: 'Layers' }]} />
      {tab === 'widgets' ? <Widgets /> : <Layers />}
    </section>
  );
}

function Widgets() {
  const logoUrl = useApp((s) => s.ws?.masjid.logo);
  const [q, setQ] = useState('');
  const list = WIDGET_ORDER.filter((t) => WIDGETS[t].label.toLowerCase().includes(q.trim().toLowerCase()));
  const [dragging, setDragging] = useState<{ type: WidgetType; x: number; y: number } | null>(null);
  const start = useRef<{ type: WidgetType; x: number; y: number; id: number } | null>(null);

  const add = (type: WidgetType) => {
    const st = useStudio.getState();
    const d = st.design!;
    const w = createWidget(type, placeNew(d.widgets, type, d.cols, d.rows));
    Object.assign(w, keepShape(w, w, d, 'h'));
    st.change((x) => ({ ...x, widgets: [...x.widgets, w] }));
    st.select(w.id);
    useStudioPanels.getState().set({ library: false });
    if (type === 'logo' && !useApp.getState().ws?.masjid.logo) openSheet({ name: 'logo' });
  };

  const onDown = (e: React.PointerEvent, type: WidgetType) => {
    if (e.button !== 0) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    start.current = { type, x: e.clientX, y: e.clientY, id: e.pointerId };
  };
  const onMove = (e: React.PointerEvent) => {
    const s = start.current;
    if (!s) return;
    if (!dragging && Math.hypot(e.clientX - s.x, e.clientY - s.y) < 5) return;
    setDragging({ type: s.type, x: e.clientX, y: e.clientY });
    useDnd.setState({ type: s.type, x: e.clientX, y: e.clientY });
  };
  const onUp = (e: React.PointerEvent) => {
    const s = start.current;
    start.current = null;
    if (!s) return;
    if (dragging) {
      useDnd.getState().dropAt?.(s.type, e.clientX, e.clientY);
      setDragging(null);
      useDnd.setState({ type: null });
    } else add(s.type);
  };
  const onCancel = () => { start.current = null; setDragging(null); useDnd.setState({ type: null }); };

  return (
    <>
      <label className="lib-search"><Icon name="search" size={18} /><input className="bare" placeholder="Search widgets" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search widgets" /></label>
      <ScrollArea className="wgrid-area" innerClassName="wgrid" label="Widgets">
        {list.map((t) => (
          <button key={t} type="button" className={`wtile ctl${dragging?.type === t ? ' lifting' : ''}`} onPointerDown={(e) => onDown(e, t)} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onCancel}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); add(t); } }} aria-label={`Add ${WIDGETS[t].label}`} title={`Drag onto the grid, or click to add ${WIDGETS[t].label}`}>
            {t === 'logo' && logoUrl ? <span className="wic has-logo"><img src={logoUrl} alt="" /></span> : <span className="wic"><Icon name={WIDGETS[t].icon} size={24} /></span>}
            <span>{WIDGETS[t].label}</span>
          </button>
        ))}
        {!list.length && <p className="hint" style={{ gridColumn: '1 / -1', textAlign: 'center' }}>No widgets match “{q}”.</p>}
      </ScrollArea>
      <p className="lib-hint"><Icon name="hand" size={18} />Drag onto the grid. Widgets snap to cells.</p>
      {dragging && createPortal(
        <div className="drag-chip glass" style={{ transform: `translate3d(${dragging.x + 14}px, ${dragging.y + 14}px, 0)` }} aria-hidden>
          <span className="wic"><Icon name={WIDGETS[dragging.type].icon} size={20} /></span>{WIDGETS[dragging.type].label}
        </div>, document.body)}
    </>
  );
}

function Layers() {
  const design = useStudio((s) => s.design)!;
  const selected = useStudio((s) => s.selected);
  const st = useStudio.getState;
  // Top of the list is in front, which is the end of the widgets array.
  const items = [...design.widgets].reverse();
  const [dragId, setDragId] = useState<string | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [renaming, setRenaming] = useState<string | null>(null);

  const move = (id: string, toListIndex: number) => {
    st().change((d) => {
      const arr = [...d.widgets].reverse();
      const from = arr.findIndex((w) => w.id === id);
      if (from < 0) return d;
      const [w] = arr.splice(from, 1);
      arr.splice(Math.max(0, Math.min(arr.length, toListIndex > from ? toListIndex - 1 : toListIndex)), 0, w!);
      return { ...d, widgets: arr.reverse() };
    });
  };
  const indexAt = (y: number) => {
    const rows = [...(listRef.current?.children ?? [])] as HTMLElement[];
    for (let i = 0; i < rows.length; i++) { const r = rows[i]!.getBoundingClientRect(); if (y < r.top + r.height / 2) return i; }
    return rows.length;
  };

  return (
    <>
      <p className="hint" style={{ margin: '-4px 0 0' }}>{design.widgets.length} widget{design.widgets.length === 1 ? '' : 's'} on this screen</p>
      {!items.length && <div className="empty"><span>No widgets yet. Add some from the Widgets tab.</span></div>}
      <ScrollArea className="layers-area">
      <ul className="layers" ref={listRef}>
        {items.map((w, i) => (
          <li key={w.id} className={`layer ctl${w.id === selected ? ' sel' : ''}${w.hidden ? ' hid' : ''}${dragId === w.id ? ' dragging' : ''}${overIdx === i && dragId ? ' over' : ''}`}
            onClick={() => st().select(w.id)}>
            <span className="grip" title="Drag to reorder" aria-label={`Reorder ${widgetName(w)}`} role="button" tabIndex={0}
              onPointerDown={(e) => { e.preventDefault(); (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); setDragId(w.id); }}
              onPointerMove={(e) => { if (dragId) setOverIdx(indexAt(e.clientY)); }}
              onPointerUp={() => { if (dragId && overIdx != null) move(dragId, overIdx); setDragId(null); setOverIdx(null); }}
              onKeyDown={(e) => { if (e.key === 'ArrowUp') { e.preventDefault(); move(w.id, Math.max(0, i - 1)); } if (e.key === 'ArrowDown') { e.preventDefault(); move(w.id, i + 2); } }}>
              <Icon name="grip" size={18} />
            </span>
            <span className="lic"><Icon name={WIDGETS[w.type].icon} size={20} /></span>
            <span className="lname" onDoubleClick={(e) => { e.stopPropagation(); setRenaming(w.id); }}>
              {renaming === w.id ? (
                <input className="bare" autoFocus defaultValue={widgetName(w)} maxLength={60} aria-label="Widget name"
                  onBlur={(e) => { st().updateWidget(w.id, { name: e.target.value.trim() || undefined }); setRenaming(null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setRenaming(null); }} onClick={(e) => e.stopPropagation()} />
              ) : <b>{widgetName(w)}</b>}
              <small>{w.w} × {w.h} cells{w.hidden ? ' · Hidden' : ''}</small>
            </span>
            <button type="button" className="lbtn" aria-label={w.hidden ? `Show ${widgetName(w)}` : `Hide ${widgetName(w)}`} aria-pressed={!w.hidden}
              onClick={(e) => { e.stopPropagation(); st().updateWidget(w.id, { hidden: !w.hidden }); }}><Icon name={w.hidden ? 'eyeOff' : 'eye'} size={20} /></button>
            <button type="button" className={`lbtn${w.locked ? ' on' : ''}`} aria-label={w.locked ? `Unlock ${widgetName(w)}` : `Lock ${widgetName(w)}`} aria-pressed={!!w.locked}
              onClick={(e) => { e.stopPropagation(); st().updateWidget(w.id, { locked: !w.locked }); }}><Icon name={w.locked ? 'lock' : 'unlock'} size={18} /></button>
          </li>
        ))}
      </ul>
      </ScrollArea>
      <p className="lib-hint"><Icon name="grip" size={18} />Drag to reorder. Top of the list sits in front.</p>
    </>
  );
}
